/**
 * JSON export and import functionality for stage layouts
 * Allows saving and loading stage designs as JSON files
 */

import type { StageLayout } from '@/stage/types';
import { downloadBlob } from './png-export';

/**
 * Export layout as a downloadable JSON file
 */
export function exportLayoutJson(
  layout: StageLayout,
  filename?: string
): void {
  const json = JSON.stringify(layout, null, 2);
  const blob = new Blob([json], { type: 'application/json' });

  const finalFilename =
    filename || `${layout.metadata.name || 'stage'}-layout.json`;

  downloadBlob(blob, finalFilename);
}

/**
 * Import layout from JSON file selected by user
 */
export async function importLayoutJson(): Promise<StageLayout | null> {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';

    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) {
        resolve(null);
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const content = event.target?.result as string;
          const data = JSON.parse(content);

          const validation = validateImportedLayout(data);
          if (!validation.valid) {
            console.error('Invalid layout file:', validation.errors);
            resolve(null);
            return;
          }

          resolve(data as StageLayout);
        } catch (error) {
          console.error('Failed to parse JSON:', error);
          resolve(null);
        }
      };

      reader.onerror = () => {
        console.error('Failed to read file');
        resolve(null);
      };

      reader.readAsText(file);
    };

    input.click();
  });
}

/**
 * Validate imported JSON to ensure it's a valid StageLayout
 */
export function validateImportedLayout(
  data: unknown
): { valid: boolean; errors?: string[] } {
  const errors: string[] = [];

  if (!data || typeof data !== 'object') {
    return { valid: false, errors: ['Data must be an object'] };
  }

  const obj = data as Record<string, unknown>;

  // Check required root properties
  if (!obj.version || typeof obj.version !== 'string') {
    errors.push('Missing or invalid "version" field');
  }

  if (!obj.canvas || typeof obj.canvas !== 'object') {
    errors.push('Missing or invalid "canvas" field');
  } else {
    const canvas = obj.canvas as Record<string, unknown>;
    if (
      typeof canvas.width_m !== 'number' ||
      typeof canvas.height_m !== 'number' ||
      typeof canvas.grid_size_m !== 'number'
    ) {
      errors.push('Invalid canvas configuration');
    }
  }

  if (!Array.isArray(obj.objects)) {
    errors.push('Missing or invalid "objects" array');
  }

  if (!Array.isArray(obj.shooting_positions)) {
    errors.push('Missing or invalid "shooting_positions" array');
  }

  if (!Array.isArray(obj.walls)) {
    errors.push('Missing or invalid "walls" array');
  }

  if (!Array.isArray(obj.movement_paths)) {
    errors.push('Missing or invalid "movement_paths" array');
  }

  if (!Array.isArray(obj.annotations)) {
    errors.push('Missing or invalid "annotations" array');
  }

  if (!obj.metadata || typeof obj.metadata !== 'object') {
    errors.push('Missing or invalid "metadata" field');
  } else {
    const metadata = obj.metadata as Record<string, unknown>;
    if (typeof metadata.name !== 'string') {
      errors.push('Metadata must have a "name" field');
    }
  }

  return {
    valid: errors.length === 0,
    errors: errors.length > 0 ? errors : undefined,
  };
}
